import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
} from '../../../../components/drawer';
import { Form } from '../../../../components/form/form';
import { useViewer } from '../../../../context/viewer';
import { DisabledWithTooltip } from '../../../../components/permissions/DisabledWithTooltip';
import { LibraryExercise } from '../../exercises/types';
import { LibraryExerciseMaxConversion } from '../types';
import {
  ExerciseMaxConversionFormSchema,
  ExerciseMaxConversionFormType,
  ratioToPercentage,
} from '../utils';
import { ExerciseMaxConversionSheetContent } from './MaxConversionSheetContent';

export const EditExerciseMaxConversionSheet = ({
  exerciseMaxConversion,
  exercises,
  open,
  onOpenChange,
  onSubmit,
  onDelete,
}: {
  exerciseMaxConversion: LibraryExerciseMaxConversion;
  exercises: LibraryExercise[];
  open: boolean;
  onOpenChange: () => void;
  onSubmit: (id: string, data: ExerciseMaxConversionFormType) => void;
  onDelete: (id: string) => void;
}) => {
  const { t } = useTranslation();
  const {
    org: {
      settings: { enableTeamLibrary },
    },
    team: { selectedTeam, teams },
    hasPermissions,
  } = useViewer();
  const disableEdit = !hasPermissions('library.exerciseMaxConversions', 'edit');
  const disableDelete = !hasPermissions(
    'library.exerciseMaxConversions',
    'delete',
  );

  const teamName = enableTeamLibrary
    ? teams.find((t) => t.id === selectedTeam)?.name
    : undefined;
  const form = useForm<ExerciseMaxConversionFormType>({
    resolver: zodResolver(ExerciseMaxConversionFormSchema),
    defaultValues: {
      toLibraryExerciseId: exerciseMaxConversion.toLibraryExerciseId,
      fromLibraryExerciseId: exerciseMaxConversion.fromLibraryExerciseId,
      percentage: ratioToPercentage(exerciseMaxConversion.ratio),
    },
  });
  const { handleSubmit } = form;

  return (
    <Drawer open={open} onClose={() => onOpenChange()} anchor="right">
      <DrawerHeader title={t('editConversion')} onClose={() => onOpenChange()}>
        {teamName && (
          <Typography variant="body2" color="text.secondary">
            {teamName}
          </Typography>
        )}
      </DrawerHeader>

      <Form {...form}>
        <form
          id="edit-max-conversion-form"
          onSubmit={handleSubmit((e) => {
            onSubmit(exerciseMaxConversion.id, e);
            onOpenChange();
          })}
          className="flex flex-col grow h-full overflow-hidden"
        >
          <div className="px-8 py-4 grow overflow-y-scroll space-y-4">
            <ExerciseMaxConversionSheetContent
              form={form}
              exercises={exercises}
              mode="edit"
            />
          </div>
        </form>
      </Form>
      <DrawerFooter className="flex flex-row w-full p-8 justify-between">
        <DisabledWithTooltip
          disabled={disableDelete}
          reason={t('permissionDisabled.delete', {
            item: t('maxConversions'),
          })}
        >
          <Button
            variant="containedDestructive"
            onClick={() => {
              onDelete(exerciseMaxConversion.id);
              onOpenChange();
            }}
          >
            {t('delete')}
          </Button>
        </DisabledWithTooltip>
        <div className="inline-flex gap-2">
          <Button variant="outlined" onClick={() => onOpenChange()}>
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            form="edit-max-conversion-form"
            disabled={disableEdit}
          >
            {t('save')}
          </Button>
        </div>
      </DrawerFooter>
    </Drawer>
  );
};
